import { jsx, jsxs } from "react/jsx-runtime";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Flex, Grid, Image, Typography } from "antd";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom";
import { Loader, withScrollToTop, useTranslation } from "@inscore/shared";
import StatsApi from "./../../../api/StatsApi.js";
import { ReactComponent as ArrowLeft } from "./../../../assets/icons/arrow-left.svg";
import { translateOr } from "../statsService";
import StatsFilter, {
  StatsRangeFilter
} from "../statsTable/statsFilter/StatsFilter";
import "./statsTeamFixtures.css";
const { Text, Title } = Typography;
const { useBreakpoint } = Grid;
const SIDE_TO_LOCATION = { all: 0, home: 1, away: 2 };
const PERIOD_TO_API = { match: 0, firstHalf: 1, secondHalf: 2 };
const DEFAULT_FILTER_OPTIONS = [
  {
    key: "side",
    label: "Side",
    selectedKey: "all",
    kind: 0,
    options: [
      { key: "all", label: "All" },
      { key: "home", label: "Home" },
      { key: "away", label: "Away" }
    ]
  },
  {
    key: "period",
    label: "Period",
    selectedKey: "match",
    kind: 1,
    options: [
      { key: "match", label: "Match" },
      { key: "firstHalf", label: "1st Half" },
      { key: "secondHalf", label: "2nd Half" }
    ]
  }
];
const FILTER_KEYS = ["side", "period", "total", "minuteRange"];
const TeamLine = ({ logo, name, score, highlighted }) => /* @__PURE__ */ jsxs(Flex, { align: "center", justify: "space-between", gap: 12, children: [
  /* @__PURE__ */ jsxs(Flex, { align: "center", gap: 12, style: { minWidth: 0 }, children: [
    /* @__PURE__ */ jsx(
      Image,
      {
        src: logo,
        alt: name,
        width: 24,
        height: 24,
        preview: false,
        style: { objectFit: "contain", flexShrink: 0 }
      }
    ),
    /* @__PURE__ */ jsx(
      Text,
      {
        className: "truncate-line",
        style: { fontWeight: highlighted ? 600 : 400 },
        children: name
      }
    )
  ] }),
  /* @__PURE__ */ jsx(Text, { strong: true, children: score })
] });
const StatsTeamFixtures = () => {
  var _a, _b;
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();
  const location = useLocation();
  const { teamId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const getFixturesApi = StatsApi.getSpecialStatsFixtures();
  const { team, subjectLabel, filterOptions } = (_a = location.state) != null ? _a : {};
  const seasonId = +searchParams.get("s");
  const metric = searchParams.get("m");
  const filters = useMemo(
    () => Object.fromEntries(
      FILTER_KEYS.filter((key) => searchParams.has(key)).map(
        (key) => [key, searchParams.get(key)]
      )
    ),
    [searchParams]
  );
  useEffect(() => {
    seasonId && teamId && metric !== null && getFixtures();
    return () => {
      getFixturesApi.cancel();
    };
  }, [seasonId, teamId, metric, filters]);
  const getFixtures = async () => {
    setLoading(true);
    try {
      const response = await getFixturesApi.call({
        seasonId,
        teamId,
        metric,
        location: SIDE_TO_LOCATION[filters.side],
        period: PERIOD_TO_API[filters.period],
        total: filters.total,
        minuteRange: filters.minuteRange
      });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching stats fixtures:", error);
    } finally {
      setLoading(false);
    }
  };
  const teamInfo = useMemo(() => {
    var _a2;
    if (team)
      return team;
    const item = (_a2 = data == null ? void 0 : data.items) == null ? void 0 : _a2[0];
    if (!item)
      return null;
    return String(item.homeTeamId) === teamId ? { name: item.homeTeamName, icon: item.homeTeamLogo } : { name: item.awayTeamName, icon: item.awayTeamLogo };
  }, [team, data, teamId]);
  const onFilterChange = (key, value) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next, { replace: true, state: location.state });
  };
  const onBackClick = () => {
    if (location.key === "default") {
      navigate("/stats");
    } else {
      navigate(-1);
    }
  };
  const options = filterOptions != null ? filterOptions : DEFAULT_FILTER_OPTIONS;
  const items = (_b = data == null ? void 0 : data.items) != null ? _b : [];
  return /* @__PURE__ */ jsxs("div", { className: "stats-team-fixtures", children: [
    loading && /* @__PURE__ */ jsx(Loader, { overlay: true }),
    /* @__PURE__ */ jsxs(Flex, { vertical: true, gap: isMobile ? 12 : 16, children: [
      /* @__PURE__ */ jsxs(Flex, { align: "center", gap: 12, children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "stats-team-fixtures__back",
            onClick: onBackClick,
            "aria-label": "Back",
            children: /* @__PURE__ */ jsx(ArrowLeft, {})
          }
        ),
        (teamInfo == null ? void 0 : teamInfo.icon) && /* @__PURE__ */ jsx(
          Image,
          {
            src: teamInfo.icon,
            alt: teamInfo.name,
            width: 32,
            height: 32,
            preview: false,
            style: { objectFit: "contain", flexShrink: 0 }
          }
        ),
        /* @__PURE__ */ jsxs(Flex, { vertical: true, style: { minWidth: 0 }, children: [
          /* @__PURE__ */ jsx(
            Title,
            {
              level: isMobile ? 5 : 4,
              className: "truncate-line",
              style: { margin: 0 },
              children: teamInfo == null ? void 0 : teamInfo.name
            }
          ),
          /* @__PURE__ */ jsx(Text, { type: "secondary", children: translateOr(
            t,
            `stats_subject_${metric}`,
            subjectLabel
          ) })
        ] })
      ] }),
      /* @__PURE__ */ jsx(
        StatsFilter,
        {
          filterOptions: options,
          filters,
          onFilterChange
        }
      ),
      /* @__PURE__ */ jsx(
        StatsRangeFilter,
        {
          filterOptions: options,
          filters,
          onFilterChange
        }
      ),
      !loading && !items.length ? /* @__PURE__ */ jsx(Text, { type: "secondary", children: t("no_data_yet") }) : /* @__PURE__ */ jsx(Flex, { vertical: true, children: items.map((item, index) => /* @__PURE__ */ jsxs(Fragment, { children: [
        index > 0 && /* @__PURE__ */ jsx("div", { className: "stats-team-fixtures__divider" }),
        /* @__PURE__ */ jsxs(
          Flex,
          {
            vertical: true,
            gap: 8,
            className: "stats-team-fixtures__item",
            onClick: () => navigate(`/fixture/${item.fixtureId}`),
            children: [
              /* @__PURE__ */ jsx(
                TeamLine,
                {
                  logo: item.homeTeamLogo,
                  name: item.homeTeamName,
                  score: item.homeResult,
                  highlighted: String(item.homeTeamId) === teamId
                }
              ),
              /* @__PURE__ */ jsx(
                TeamLine,
                {
                  logo: item.awayTeamLogo,
                  name: item.awayTeamName,
                  score: item.awayResult,
                  highlighted: String(item.awayTeamId) === teamId
                }
              )
            ]
          }
        )
      ] }, item.fixtureId)) })
    ] })
  ] });
};
var stdin_default = withScrollToTop(StatsTeamFixtures);
export {
  stdin_default as default
};
